import { createContext, useState, useMemo, useContext, useEffect } from "react";
import {
	ThemeProvider,
	createTheme,
	CssBaseline,
	GlobalStyles,
} from "@mui/material";

const ThemeContext = createContext({
	toggleColorMode: () => {},
	darkMode: false,
});

export const ThemeContextProvider = ({ children }) => {
	const [darkMode, setDarkMode] = useState(() => {
		return localStorage.getItem("darkMode") === "true";
	});

	const activeColor = darkMode ? "#ff4d00" : "#ff4d00";
	useEffect(() => {
		localStorage.setItem("darkMode", darkMode);
	}, [darkMode]);

	const theme = useMemo(
		() =>
			createTheme({
				typography: {
					fontSize: 15,
				},
				palette: {
					mode: darkMode ? "dark" : "light",
					background: {
						default: darkMode ? "#121212" : "#f5f5f5",
						paper: darkMode ? "#1e1e1e" : "#ffffff",
						positive: darkMode ? "#3F522B" : "#D4E1BC",
						negative: darkMode ? "#5A2C2C" : "#F4C5C5",
					},
					text: {
						primary: darkMode ? "#ffffff" : "#000000",
						secondary: darkMode ? "#aaaaaa" : "#555555",
						contrast: darkMode ? "#121212" : "#f5f5f5",
					},
					border: {
						main: darkMode ? "#333" : "#ddd",
					},
					active: {
						main: activeColor,
					},
					values: {
						positive: darkMode ? "#B6E57E" : "#4C721D",
						negative: darkMode ? "#F58A8A  " : "#8C0C0C",
						investment: darkMode ? "#4F7DF5" : "#4F7DF5",
					},
					error: {
						main: "#d32f2f",
					},
					categories: {
						default: darkMode ? "#aaaaaa" : "#555555",
					},
				},
				components: {
					MuiButton: {
						styleOverrides: {
							root: {
								borderRadius: "8px",
								textTransform: "none",
								fontWeight: "bold",
								padding: "8px 16px",
							},
							textPrimary: {
								color: darkMode ? "#ffffff" : "#000000",
								"&:hover": {
									backgroundColor: darkMode ? "#333" : "#ddd",
								},
							},
							containedPrimary: {
								color: darkMode ? "#ffffff" : "#000000",
								backgroundColor: activeColor,
								"&:hover": {
									backgroundColor: darkMode ? "#333" : "#ddd",
								},
							},
							containedSecondary: {
								backgroundColor: "#3399ff",
								"&:hover": {
									backgroundColor: "#1e87e5",
								},
							},
							outlinedPrimary: {
								color: darkMode ? "#AAA" : "#555",
								borderColor: darkMode ? "#AAA" : "#555",
								backgroundColor: darkMode
									? "#1e1e1e"
									: "#ffffff",
								"&:hover": {
									backgroundColor: darkMode ? "#333" : "#ddd",
									borderColor: darkMode ? "#555" : "#AAA",
								},
							},
						},
					},
					MuiPaper: {
						styleOverrides: {
							root: {
								"--Paper-overlay": "none",
								backgroundImage: "none",
							},
						},
					},
					MuiDialog: {
						styleOverrides: {
							paper: {
								borderRadius: "10px",
								backgroundColor: darkMode
									? "#1e1e1e"
									: "#ffffff",
							},
						},
					},
					MuiTextField: {
						defaultProps: {
							variant: "outlined",
							size: "small",
						},
					},
					MuiSelect: {
						defaultProps: {
							variant: "outlined",
							size: "small",
						},
					},
					MuiFormControl: {
						defaultProps: {
							size: "small",
						},
					},
					MuiPickersPopper: {
						styleOverrides: {
							paper: {
								borderRadius: "10px",
							},
						},
					},
					MuiMenu: {
						styleOverrides: {
							paper: {
								borderRadius: "10px",
							},
						},
					},
					MuiMenuItem: {
						styleOverrides: {
							root: {
								marginLeft: "8px",
								marginRight: "8px",
								borderRadius: "10px",
							},
						},
					},
					MuiOutlinedInput: {
						styleOverrides: {
							root: {
								"&.Mui-focused .MuiOutlinedInput-notchedOutline":
									{
										borderColor: activeColor,
									},
							},
							notchedOutline: {
								borderColor: darkMode ? "#aaa" : "#555",
							},
						},
					},
					MuiInputLabel: {
						styleOverrides: {
							root: {
								"&.Mui-focused": {
									color: activeColor,
								},
							},
						},
					},
					MuiCheckbox: {
						styleOverrides: {
							root: {
								color: darkMode ? "#aaa" : "#555",
								"&.Mui-checked": {
									color: activeColor,
								},
								"&:hover": {
									backgroundColor: `${activeColor}22`,
								},
							},
						},
					},
					MuiRadio: {
						styleOverrides: {
							root: {
								color: darkMode ? "#aaa" : "#555",
								"&.Mui-checked": {
									color: activeColor,
								},
								"&:hover": {
									backgroundColor: `${activeColor}22`,
								},
							},
						},
					},
					MuiFormLabel: {
						styleOverrides: {
							root: {
								color: darkMode ? "#aaa" : "#555",
								"&.Mui-focused": {
									color: activeColor,
								},
							},
						},
					},
				},
			}),
		[darkMode, activeColor],
	);

	const toggleColorMode = () => setDarkMode((prev) => !prev);

	return (
		<ThemeContext.Provider value={{ toggleColorMode, darkMode }}>
			<ThemeProvider theme={theme}>
				<CssBaseline />
				<GlobalStyles
					styles={{
						"#root": {
							backgroundColor: theme.palette.background.default,
							color: theme.palette.text.primary,
							minHeight: "100vh",
							transition: "all 0.3s ease-in-out",
						},
					}}
				/>
				{children}
			</ThemeProvider>
		</ThemeContext.Provider>
	);
};

export const useThemeContext = () => useContext(ThemeContext);
